import React, { useState, useEffect } from "react";
import { API_BASE_URL, enhancedFetch } from "../services/api";

const ScriptResults = ({ token, onUnauthorized, setError, setSuccess }) => {
  const [results, setResults] = useState([]);
  const [selectedResult, setSelectedResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setErrorFlg] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10); // 每页显示10个结果
  const [showDetailModal, setShowDetailModal] = useState(false); // 控制详情弹窗显示

  const handleError = (error) => {
    setError(error);
    setErrorFlg(error);
  };

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        handleError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // 获取结果列表
  useEffect(() => {
    const fetchResults = async () => {
      setLoading(true);
      handleError(null);

      try {
        const response = await enhancedFetch(
          `${API_BASE_URL}/scripts/results/`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
          onUnauthorized
        );
        if (!response.ok) {
          throw new Error("获取结果失败");
        }
        const data = await response.json();
        setResults(data);
        setCurrentPage(1); // 重置到第一页
      } catch (err) {
        handleError("加载结果失败: " + err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchResults();
  }, [token, onUnauthorized]);

  // 获取单个结果详情
  const fetchResultDetail = async (resultId) => {
    try {
      const response = await enhancedFetch(
        `${API_BASE_URL}/scripts/results/${resultId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
        onUnauthorized
      );
      if (!response.ok) {
        throw new Error("获取结果详情失败");
      }
      const data = await response.json();
      setSelectedResult(data);
      setShowDetailModal(true); // 显示详情弹窗
    } catch (err) {
      handleError("加载结果详情失败: " + err.message);
    }
  };

  // 刷新结果列表
  const handleRefresh = async () => {
    setLoading(true);
    handleError(null);

    try {
      const response = await enhancedFetch(
        `${API_BASE_URL}/scripts/results/`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
        onUnauthorized
      );
      if (!response.ok) {
        throw new Error("获取结果失败");
      }
      const data = await response.json();
      setResults(data);
      setSelectedResult(null);
      setCurrentPage(1); // 重置到第一页
    } catch (err) {
      handleError("删除执行结果失败: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  // 格式化时间戳
  const formatTime = (timestamp) => {
    // 处理不同的时间格式
    if (!timestamp) return "未知";

    let date;
    if (typeof timestamp === "string") {
      // 如果是ISO字符串格式
      date = new Date(timestamp);
    } else if (typeof timestamp === "number") {
      // 如果是Unix时间戳（秒或毫秒）
      if (timestamp > 10000000000) {
        // 毫秒时间戳
        date = new Date(timestamp);
      } else {
        // 秒时间戳
        date = new Date(timestamp * 1000);
      }
    } else if (timestamp instanceof Date) {
      // 如果已经是Date对象
      date = timestamp;
    } else {
      // 尝试直接创建Date对象
      date = new Date(timestamp);
    }

    // 检查日期是否有效
    if (isNaN(date.getTime())) {
      return "无效日期";
    }

    return date.toLocaleString();
  };

  // 根据搜索词过滤结果
  const filteredResults = results.filter((result) => {
    if (!searchTerm) return true;

    const term = searchTerm.toLowerCase();
    return (
      (result.script_name && result.script_name.toLowerCase().includes(term)) ||
      (result.status && result.status.toLowerCase().includes(term)) ||
      formatTime(result.execution_time).toLowerCase().includes(term)
    );
  });

  // 计算当前页的结果
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentResults = filteredResults.slice(
    indexOfFirstItem,
    indexOfLastItem
  );
  const totalPages = Math.ceil(filteredResults.length / itemsPerPage);

  // 切换页面
  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // 上一页
  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  // 下一页
  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // 关闭详情弹窗
  const closeDetailModal = () => {
    setShowDetailModal(false);
    setSelectedResult(null);
  };

  return (
    <>
      {/* 详情弹窗 */}
      {showDetailModal && selectedResult && (
        <div className="modal-overlay" onClick={closeDetailModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>结果详情</h3>
              <button className="modal-close" onClick={closeDetailModal}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="result-detail">
                <div className="result-summary card">
                  <div className="summary-grid">
                    <div className="summary-item">
                      <strong>脚本名称:</strong>
                      <span>{selectedResult.script_name}</span>
                    </div>
                    <div className="summary-item">
                      <strong>执行ID:</strong>
                      <span className="monospace-text">
                        {selectedResult.id}
                      </span>
                    </div>
                    <div className="summary-item">
                      <strong>执行时间:</strong>
                      <span>{formatTime(selectedResult.execution_time)}</span>
                    </div>
                    <div className="summary-item">
                      <strong>状态:</strong>
                      <span
                        className={`status-badge status-${selectedResult.status.toLowerCase()} status-pill`}
                      >
                        {selectedResult.status}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="raw-json-container">
                  <h4>原始响应数据:</h4>
                  <pre className="result-output">
                    {JSON.stringify(selectedResult, null, 2)}
                  </pre>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="primary-button" onClick={closeDetailModal}>
                关闭
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="card">
        <div className="card-header">
          <h2>脚本执行结果</h2>
          <p>查看之前执行的脚本结果</p>
        </div>

        <div className="card toolbar-card">
          <div className="toolbar">
            <div className="search-container mobile-full-width">
              <div className="form-group">
                <label htmlFor="result-search">搜索结果:</label>
                <input
                  type="text"
                  id="result-search"
                  placeholder="按脚本名、状态或时间搜索..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input full-width"
                />
              </div>
            </div>
            <button
              onClick={handleRefresh}
              disabled={loading}
              className="primary-button refresh-button"
              aria-label="刷新结果列表"
            >
              {loading ? (
                <span className="button-content">
                  <span className="spinner" aria-hidden="true"></span>
                  <span className="button-text">刷新中...</span>
                </span>
              ) : (
                <span className="button-content">
                  <span className="button-text">刷新</span>
                </span>
              )}
            </button>
          </div>
        </div>

        <div className="results-list">
          <h3>结果列表 (共 {filteredResults.length} 项)</h3>
          {loading && filteredResults.length === 0 ? (
            <div className="loading-placeholder">
              <p>正在加载结果...</p>
            </div>
          ) : filteredResults.length > 0 ? (
            <>
              {/* 响应式表格形式展示结果 */}
              <div>
                <table className="results-table desktop-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>脚本名称</th>
                      <th>执行时间</th>
                      <th>状态</th>
                      <th>操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentResults.map((result) => (
                      <tr
                        key={result.id}
                        className={
                          selectedResult && selectedResult.id === result.id
                            ? "selected"
                            : ""
                        }
                      >
                        <td>{result.id}</td>
                        <td>{result.script_name}</td>
                        <td>{formatTime(result.execution_time)}</td>
                        <td>
                          <span
                            className={`status-badge status-${result.status.toLowerCase()}`}
                          >
                            {result.status}
                          </span>
                        </td>
                        <td>
                          <button
                            onClick={() => fetchResultDetail(result.id)}
                            className="link-button"
                          >
                            查看详情
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* 增强的移动端垂直布局 */}
                <div className="mobile-results">
                  {currentResults.map((result) => (
                    <div
                      key={result.id}
                      className={`result-card ${
                        selectedResult && selectedResult.id === result.id
                          ? "selected"
                          : ""
                      }`}
                      onClick={() => fetchResultDetail(result.id)}
                      role="button"
                      tabIndex={0}
                      aria-pressed="false"
                    >
                      <div className="result-card-header">
                        <h4>{result.script_name}</h4>
                        <span
                          className={`status-badge status-${result.status.toLowerCase()} status-pill`}
                        >
                          {result.status}
                        </span>
                      </div>
                      <div className="result-card-body">
                        <div className="result-info-item">
                          <span className="info-label">执行ID:</span>
                          <span className="info-value monospace-text">
                            {result.id}
                          </span>
                        </div>
                        <div className="result-info-item">
                          <span className="info-label">执行时间:</span>
                          <span className="info-value">
                            {formatTime(result.execution_time)}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <div className="empty-state">
              <p>未找到结果。执行一些脚本以在此处查看结果。</p>
            </div>
          )}
        </div>

        {/* 分页控件 */}
        {totalPages > 1 && (
          <div className="pagination">
            <button
              onClick={prevPage}
              disabled={currentPage === 1}
              className="pagination-button"
            >
              上一页
            </button>

            {[...Array(totalPages).keys()].map((number) => {
              // 当总页数超过3页时，只显示3页：第一页、当前页、最后一页
              if (totalPages > 3) {
                // 始终显示第一页
                if (number === 0) {
                  return (
                    <button
                      key={number + 1}
                      onClick={() => paginate(number + 1)}
                      className={`pagination-button page-number ${
                        currentPage === number + 1 ? "active" : ""
                      }`}
                    >
                      {number + 1}
                    </button>
                  );
                }

                // 当前页左边显示省略号
                if (number === currentPage - 2 && currentPage > 2) {
                  return (
                    <span
                      key={`ellipsis-${number}`}
                      className="pagination-ellipsis"
                    >
                      ...
                    </span>
                  );
                }

                // 显示当前页
                if (number === currentPage - 1) {
                  return (
                    <button
                      key={number + 1}
                      onClick={() => paginate(number + 1)}
                      className={`pagination-button page-number ${
                        currentPage === number + 1 ? "active" : ""
                      }`}
                    >
                      {number + 1}
                    </button>
                  );
                }

                // 当前页右边显示省略号
                if (number === currentPage && currentPage < totalPages - 1) {
                  return (
                    <span
                      key={`ellipsis-${number}`}
                      className="pagination-ellipsis"
                    >
                      ...
                    </span>
                  );
                }

                // 始终显示最后一页
                if (number === totalPages - 1) {
                  return (
                    <button
                      key={number + 1}
                      onClick={() => paginate(number + 1)}
                      className={`pagination-button page-number ${
                        currentPage === number + 1 ? "active" : ""
                      }`}
                    >
                      {number + 1}
                    </button>
                  );
                }

                return null;
              } else {
                // 总页数不超过3页时，显示所有页码
                return (
                  <button
                    key={number + 1}
                    onClick={() => paginate(number + 1)}
                    className={`pagination-button page-number ${
                      currentPage === number + 1 ? "active" : ""
                    }`}
                  >
                    {number + 1}
                  </button>
                );
              }
            })}

            <button
              onClick={nextPage}
              disabled={currentPage === totalPages}
              className="pagination-button"
            >
              下一页
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default ScriptResults;
